import { SlowNotice } from '@pa/ui'
import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { ScreenTitle } from '@/features/entry'

const LINES = ['Checking the clinic’s live calendar', 'Booking your appointment', 'Finishing up'] as const

/** The cosmetic progress timing (Frontend §4.10 item 6). */
export const PROGRESS_STEP_MS = 900
export const FINISH_STEP_MS = 200

/**
 * P-10 "Confirming your appointment…" (UserFlows P-10), the mockup's `ProcessingStep`: a spinner and three cosmetic
 * progress lines. They advance on timers while #10 is pending, and the last one never completes before the answer;
 * on success (`done`) the rest tick through quickly, then `onShown` moves on to P-12.
 */
export function ConfirmingStep({ done, onShown }: { done: boolean; onShown: () => void }) {
  const [i, setI] = useState(0)
  useEffect(() => {
    if (done) return
    const t = setInterval(() => {
      setI((k) => Math.min(k + 1, LINES.length - 1))
    }, PROGRESS_STEP_MS)
    return () => {
      clearInterval(t)
    }
  }, [done])
  useEffect(() => {
    if (!done) return
    if (i >= LINES.length) {
      onShown()
      return
    }
    const t = setTimeout(() => {
      setI((k) => k + 1)
    }, FINISH_STEP_MS)
    return () => {
      clearTimeout(t)
    }
  }, [done, i, onShown])
  return (
    <div className="processing" aria-busy="true">
      <div className="big-spinner" aria-hidden />
      <div>
        <ScreenTitle className="proc-title">Confirming your appointment…</ScreenTitle>
        <p className="muted" style={{ marginTop: 4 }}>
          This only takes a few seconds. Please keep this page open.
        </p>
      </div>
      <div className="proc-steps">
        {LINES.map((label, k) => (
          <div key={label} className={`proc-step ${k < i ? 'done' : k === i ? 'active' : ''}`}>
            <span className="p-ic" aria-hidden>
              {k < i ? (
                <Check strokeWidth={3} />
              ) : k === i ? (
                <span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
              ) : null}
            </span>
            {label}
          </div>
        ))}
      </div>
      <SlowNotice pending={!done} />
    </div>
  )
}
